import classNames from 'classnames';
import { isEmpty } from 'lodash';
import { CSSProperties, forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { UploadIcon } from 'tdesign-icons-react';
import { Button, Loading, MessagePlugin, Tooltip } from 'tdesign-react';

import { Upload } from '@/components/icon';
import { LibraryObjectData } from '@/interface/library';
import { EAsyncStatus } from '@/interface/media';
import { CgiErrorCode } from '@/services/cgi';
import { insertLibrary } from '@/services/library';

import { BatchUploadStatus, uploadCosSpine } from '../service';
import { getUploadSpineObject } from '../spine/utils/getUploadSpineObject';
import styles from './index.module.less';

export interface ISpineResult {
  id?: string; // 用于多人物立绘，依赖id区分
  fileName?: string;
  skel?: string;
  atlas?: string;
  json?: string;
  images?: string[];
  directoryName?: string;
  animation?: string;
  localPath?: string;
  directoryPath?: string;
  thumbnail?: string; // 缩略图
  asyncStatus?: EAsyncStatus; // 异步 处理loading
}

export interface IUploadSpineRef {
  loading: boolean;
  triggerUpload?: () => void;
}

/**
 * 用于上传spine文件
 */
const FolderUpload = forwardRef<IUploadSpineRef, {
  onChange?: (result: ISpineResult) => void;
  asyncUploadOnChange?: (result: ISpineResult) => void; // 剧情中卸载后处理图片loading恢复
  disabled?: boolean;
  isMaterial?: boolean;
  materialValue?: LibraryObjectData;
  value?: ISpineResult;
  style?: CSSProperties;
}>((props, ref) => {
      const [searchParams] = useSearchParams();
      const storyId = searchParams.get('id') || '';
      const { onChange, asyncUploadOnChange, disabled, isMaterial, materialValue, value } = props;
      const [isSupported, setIsSupported] = useState<boolean | undefined>(undefined);
      const [uploadInfo, setUploadInfo] = useState('');
      const [loading, setLoading] = useState(false);

      useEffect(() => {
        if (asyncUploadOnChange) {
          if (value?.asyncStatus === EAsyncStatus.UPLOAD_LOADING) {
            setLoading(true);
          } else {
            setLoading(false);
          }
        }
      }, [value?.asyncStatus, asyncUploadOnChange]);

      const handleAsyncLoading = useCallback(() => {
        // 设置异步上传loading状态  组件卸载后，loading state 重置
        // 所以需要上层组件传值恢复组件loading状态
        asyncUploadOnChange?.({
          asyncStatus: EAsyncStatus.UPLOAD_LOADING,
        });
      }, [asyncUploadOnChange]);

      const handleAsyncInit = useCallback(() => {
        // 设置异步上传loading状态 有onChange的地方就不需要调用者，如果没有则需要调用
        asyncUploadOnChange?.({
          asyncStatus: EAsyncStatus.UPLOAD_INIT,
        });
      }, [asyncUploadOnChange]);

      const uploadRef = useRef<HTMLInputElement>(null);

      useImperativeHandle(ref, () => ({
        loading,
        triggerUpload: () => handleUpload(),
      }));

      useEffect(() => {
        // 检查浏览器是否支持文件夹上传
        const input = document.createElement('input');
        input.type = 'file';

        // 检查是否支持 webkitdirectory 或 directory 属性
        const isWebkitDirectorySupported = 'webkitdirectory' in input;
        const isDirectorySupported = 'directory' in input;

        setIsSupported(isWebkitDirectorySupported || isDirectorySupported);
      }, []);

      const handleFolderSelect = (event: any) => {
        setLoading(true);
        const files = Array.from(event.target.files) as File[];
        const checkFiles = files?.map(e => ({
          resourceName: e?.name,
          url: e?.name,
        }));

        const obj = getUploadSpineObject(checkFiles);
        if (isEmpty(obj)) {
          setLoading(false);
          onChange?.({});
          return;
        }
        uploadSpine(files as File[]);
      };

      const uploadSpine = async (files: any) => {
        try {
          handleAsyncLoading();

          await uploadCosSpine(
            files,
            { storyId, size: 500 },
            (info: string) => {
              setUploadInfo(info);
            },
            async (result: {
              code: BatchUploadStatus,
              msg: string,
              spine: ISpineResult,
            }) => {
              const { code, msg, spine } = result;
              setLoading(false);
              if (code === BatchUploadStatus.fail
                || code === BatchUploadStatus.partFail
              ) {
                MessagePlugin.error(msg || '上传失败,请重试');
                setUploadInfo('');
                setLoading(false);
                onChange?.({});
                return;
              }
              if (code === BatchUploadStatus.success) {
                if (isMaterial) {
                  // 素材库上传
                  const postReqData = {
                    materialId: materialValue?.materialId,
                    type: materialValue?.type,
                    tabType: materialValue?.tabType,
                  };
                  const res = await insertLibrary({
                    ...postReqData,
                    batchResource: [
                      {
                        resourceName: spine?.directoryName || '',
                        url: spine?.directoryPath || '',
                        spine,
                      },
                    ],
                  });
                  setUploadInfo('');
                  setLoading(false);
                  if (res?.code === CgiErrorCode.NO_ERROR) {
                    onChange?.(spine);
                    MessagePlugin.success('上传成功');
                    return;
                  }

                  onChange?.({});
                  MessagePlugin.error(res?.message || '上传失败,请重试');
                  return;
                }

                // 正常上传
                setUploadInfo('');
                setLoading(false);
                onChange?.(spine);
                MessagePlugin.success('上传成功');
                return;
              }
            },
          );
        } catch (error) {
          MessagePlugin.error('上传异常,请刷新页面后重试');
          setLoading(false);
          onChange?.({});
          handleAsyncInit();
        }
      };

      const handleUpload = () => {
        if (disabled) return;
        if (loading) {
          MessagePlugin.warning('上传中，请稍后');
          return;
        }
        try {
          if (uploadRef?.current) {
            uploadRef.current.value = '';
            uploadRef.current?.click?.();
          } else {
            MessagePlugin.warning('刷新页面后重新上传');
          }
        } catch (error) {
          MessagePlugin.warning('刷新页面后重新上传');
        }
      };

      if (undefined !== undefined && !isSupported) {
        return (
      <div className="p-4">
        <div className="text-red-500">
          您的浏览器不支持文件夹上传功能。请使用最新版本的 Chrome、QQ浏览器。
        </div>
      </div>
        );
      }

      const config = isMaterial ? {
        content: '请选择完整的Spine工程文件夹进行上传（包含skel/atlas等文件），不支持多文件夹或单文件上传',
      } : {
        visible: !isSupported,
        content: '您的浏览器不支持文件夹上传功能。请使用最新版本的 Chrome、QQ浏览器。',
      };

      const content = (
    <div
      onClick={handleUpload}
      className={classNames({
        ['nova-disabled-opacity']: (disabled || loading) && !isMaterial,
        [styles.isMaterial]: isMaterial,
        [styles.item]: !isMaterial,
        ['nova-disabled']: (disabled || loading) && !isMaterial,
      })}
          style={{ ...props.style }}
    >
      {
        isMaterial
          ? <Button theme="default" icon={<UploadIcon />}>
            上传
            <span className={styles.info}>{uploadInfo}</span>
          </Button>
          : <>
            {loading ? (
              <Loading size="small"
                text={
                  <span className={styles.info1}>上传中{uploadInfo}</span>}
              />
            ) : (
              <div className={styles.textWrap}>
                <Upload style={{ width: 15, height: 15 }} />
                <span>上传Spine</span>
              </div>
            )}</>
      }
    </div>
      );
      return (
    <>
      {
        isMaterial
          ? <Tooltip
            showArrow
            {...config}
            theme="default"
          >
            {content}
          </Tooltip> : content
      }
      <input
        className={styles.hidden}
        ref={uploadRef}
        type="file"
        multiple
        // @ts-ignore
        webkitdirectory="webkitdirectory" // eslint-disable-line
        directory={'true'}
        onChange={handleFolderSelect}
      />
    </ >
      );
    });

export default FolderUpload;
